import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { FileCheck, Upload, ShieldCheck, DollarSign } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Modal } from '@/shared/components/ui/Modal';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { useUser } from '@/shared/hooks/useAuth';
import { getApiErrorMessage } from '@/shared/api/axios';
import {
  useTaxDeclaration, useTaxDeclarationsSummary, useSubmitDeclaration,
  useSubmitForReview, useUploadProof, useReviewDeclaration,
} from '../tax/useTaxDeclarations';
import { DECLARATION_SECTIONS, type TaxDeclaration, type TaxDeclarationStatus } from '../tax/types';
import { formatINR } from '@/shared/utils/currency';

function currentFinancialYear(): string {
  const now = new Date();
  const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1; // FY starts April
  return `${startYear}-${startYear + 1}`;
}

const STATUS_TONE: Record<TaxDeclarationStatus, 'neutral' | 'warning' | 'info' | 'success' | 'danger'> = {
  draft: 'neutral', submitted: 'warning', under_review: 'info', approved: 'success', rejected: 'danger',
};

export function TaxDeclarationsPage() {
  const user = useUser();
  const isHrReviewer = user?.role === 'superadmin' || user?.role === 'admin' || user?.department === 'hr';
  const [financialYear] = useState(currentFinancialYear());
  const [reviewTarget, setReviewTarget] = useState<TaxDeclaration | null>(null);

  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto p-6">
      <PageHeader
        icon={FileCheck}
        eyebrow="People"
        title="Tax Declarations"
        description={<>Financial Year {financialYear} · Income tax declarations and proof documents</>}
      />

      <MyDeclarationCard financialYear={financialYear} />

      {isHrReviewer && (
        <ReviewQueueCard financialYear={financialYear} onReview={setReviewTarget} />
      )}

      {reviewTarget && (
        <ReviewModal declaration={reviewTarget} onClose={() => setReviewTarget(null)} />
      )}
    </div>
  );
}

// ─── Employee self-service card ──────────────────────────────────────────────

function MyDeclarationCard({ financialYear }: { financialYear: string }) {
  const query = useTaxDeclaration(financialYear);
  const submitDraft = useSubmitDeclaration();
  const submitForReview = useSubmitForReview();
  const uploadProof = useUploadProof(query.data?._id);

  const [regime, setRegime] = useState<'old' | 'new'>(query.data?.regime ?? 'old');
  const [amounts, setAmounts] = useState<Record<string, string>>({});

  const declaration = query.data ?? null;
  const isLocked = declaration && !['draft', 'rejected'].includes(declaration.status);

  const currentAmounts = useMemo(() => {
    if (Object.keys(amounts).length > 0) return amounts;
    const initial: Record<string, string> = {};
    for (const section of DECLARATION_SECTIONS) {
      initial[section.code] = String(declaration?.declarations?.[section.code] ?? '');
    }
    return initial;
  }, [declaration, amounts]);

  const total = DECLARATION_SECTIONS.reduce((sum, s) => sum + (Number(currentAmounts[s.code]) || 0), 0);

  const handleSave = () => {
    const declarations: Record<string, number> = {};
    for (const section of DECLARATION_SECTIONS) declarations[section.code] = Number(currentAmounts[section.code]) || 0;
    submitDraft.mutate({ financialYear, regime, declarations }, {
      onSuccess: () => toast.success('Declaration saved as draft.'),
      onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to save declaration.')),
    });
  };

  const handleSubmit = () => {
    if (!declaration?._id) return;
    submitForReview.mutate(declaration._id, {
      onSuccess: () => toast.success('Declaration submitted for HR review.'),
      onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to submit declaration.')),
    });
  };

  const handleUploadProof = (code: string, label: string) => {
    const fileUrl = window.prompt(`Enter file URL for "${label}":`);
    if (!fileUrl) return;
    uploadProof.mutate({ code, label, fileUrl }, {
      onSuccess: () => toast.success('Proof document uploaded.'),
      onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to upload proof.')),
    });
  };

  if (query.isLoading) return <div className="flex justify-center p-10"><Spinner /></div>;

  return (
    <Card>
      <CardHeader
        title="My Declaration"
        subtitle="Section-wise deductions for this financial year"
        action={declaration && <Badge tone={STATUS_TONE[declaration.status]}>{declaration.status.replace('_', ' ')}</Badge>}
      />
      <CardBody className="space-y-4">
        <Select
          label="Tax Regime"
          value={regime}
          disabled={!!isLocked}
          onChange={(e) => setRegime(e.target.value as 'old' | 'new')}
          options={[{ value: 'old', label: 'Old Tax Regime' }, { value: 'new', label: 'New Tax Regime' }]}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          {DECLARATION_SECTIONS.map((section) => (
            <Input
              key={section.code}
              label={section.label}
              type="number"
              disabled={!!isLocked}
              value={currentAmounts[section.code] ?? ''}
              onChange={(e) => setAmounts({ ...currentAmounts, [section.code]: e.target.value })}
              hint={'max' in section ? `Max ${formatINR(section.max)}` : undefined}
            />
          ))}
        </div>

        <div className="flex items-center justify-between rounded-card-sm border border-outline bg-surface-container-low/40 px-4 py-2.5">
          <span className="flex items-center gap-2 text-data-sm text-on-surface-variant"><DollarSign className="h-4 w-4" /> Total Declared</span>
          <span className="text-data-lg font-semibold text-on-surface">{formatINR(total)}</span>
        </div>

        {declaration && declaration.proofDocuments.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-label-caps font-semibold uppercase text-on-surface-variant">Proof Documents</p>
            {declaration.proofDocuments.map((doc) => (
              <div key={doc._id} className="flex items-center justify-between rounded-card-sm border border-outline/50 bg-surface-container-low/40 px-3 py-1.5">
                <span className="text-data-sm text-on-surface">{doc.label}</span>
                <Badge tone={doc.status === 'verified' ? 'success' : doc.status === 'rejected' ? 'danger' : 'warning'}>{doc.status}</Badge>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t border-outline pt-3">
          <Button variant="outline" size="sm" disabled={!!isLocked} onClick={() => handleUploadProof('proof_general', 'Supporting Proof')}>
            <Upload className="h-3.5 w-3.5" /> Upload Proof
          </Button>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" disabled={!!isLocked} loading={submitDraft.isPending} onClick={handleSave}>
              Save Draft
            </Button>
            <Button size="sm" disabled={!declaration?._id || !!isLocked} loading={submitForReview.isPending} onClick={handleSubmit}>
              <ShieldCheck className="h-3.5 w-3.5" /> Submit for Review
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// ─── HR-admin review queue ────────────────────────────────────────────────────

function ReviewQueueCard({ financialYear, onReview }: { financialYear: string; onReview: (d: TaxDeclaration) => void }) {
  const query = useTaxDeclarationsSummary(financialYear, 'submitted');

  const columns: Column<TaxDeclaration>[] = [
    {
      key: 'employee', header: 'Employee',
      render: (d) => <span className="font-medium text-on-surface">{d.employeeName || '—'}</span>,
    },
    { key: 'department', header: 'Department', render: (d) => <span className="text-on-surface-variant">{d.employeeDepartment || '—'}</span> },
    { key: 'regime', header: 'Regime', render: (d) => <span className="capitalize">{d.regime}</span> },
    { key: 'total', header: 'Declared', align: 'right', render: (d) => `${formatINR(d.totalDeclared)}` },
    { key: 'status', header: 'Status', render: (d) => <Badge tone={STATUS_TONE[d.status]}>{d.status.replace('_', ' ')}</Badge> },
    {
      key: 'actions', header: 'Actions', align: 'right',
      render: (d) => <Button variant="outline" size="sm" onClick={() => onReview(d)}>Review</Button>,
    },
  ];

  return (
    <Card>
      <CardHeader title="Pending Review" subtitle="Declarations submitted by employees awaiting HR approval" />
      <CardBody>
        {query.isLoading ? (
          <div className="flex justify-center p-6"><Spinner /></div>
        ) : (query.data ?? []).length === 0 ? (
          <EmptyState icon={<FileCheck className="h-6 w-6" />} title="Nothing pending" description="No declarations are waiting for review right now." />
        ) : (
          <Table columns={columns} data={query.data ?? []} rowKey={(d) => d._id} />
        )}
      </CardBody>
    </Card>
  );
}

function ReviewModal({ declaration, onClose }: { declaration: TaxDeclaration; onClose: () => void }) {
  const review = useReviewDeclaration();
  const [notes, setNotes] = useState('');
  const [approvedAmount, setApprovedAmount] = useState(String(declaration.totalDeclared));

  const decide = (decision: 'approved' | 'rejected') => {
    review.mutate(
      { id: declaration._id, decision, notes, approvedAmount: decision === 'approved' ? Number(approvedAmount) || 0 : undefined },
      {
        onSuccess: () => { toast.success(decision === 'approved' ? 'Declaration approved.' : 'Declaration rejected.'); onClose(); },
        onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to review declaration.')),
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Review — ${declaration.employeeName}`}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="danger" loading={review.isPending} onClick={() => decide('rejected')}>Reject</Button>
          <Button loading={review.isPending} onClick={() => decide('approved')}>Approve</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-data-sm">
          {DECLARATION_SECTIONS.map((s) => (
            <div key={s.code} className="rounded-card-sm border border-outline bg-surface-container-low/40 px-3 py-2">
              <p className="text-[11px] uppercase text-on-surface-variant">{s.label}</p>
              <p className="font-medium text-on-surface">{formatINR(declaration.declarations[s.code] ?? 0)}</p>
            </div>
          ))}
        </div>
        <Input label="Approved Amount (₹)" type="number" value={approvedAmount} onChange={(e) => setApprovedAmount(e.target.value)} />
        <Input label="Notes (required for rejection)" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reviewer notes..." />
      </div>
    </Modal>
  );
}
